import React from 'react';
import { useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import { useUserContext } from '@/auth/UserContext';
import { Cell } from '@/components/ProfileMenu/Cell';
import { useProfileRestriction } from '@/components/ProfileRestriction/useProfileRestriction';
import { ArrowLeft } from '@/components/Icons/ArrowLeft';
import { Close } from '@/components/Icons/Close';
import { Toggle } from '@/components/Toggle';
import { useScrollFadeProgress } from '@/hooks/useScrollFadeProgress';
import { useUserSettings } from '@/hooks/useUserSettings';
import { ROUTE_SUBSCRIPTION } from '@/router/routes';
import { IconType, ProfileMenuView } from '@/types/components';
import { PARENT_ROLES, UserSetting } from '@/types/user';
import { getRoleLabel } from '@/utils/person';
import { getFullName, joinNonEmpty } from '@/utils/text';

interface Props {
  onClose: () => void;
  onOpenView: (view: ProfileMenuView) => void;
  onError: () => void;
  onLogout: () => void;
}

export const MenuView: React.FC<Props> = ({
  onClose,
  onOpenView,
  onError,
  onLogout,
}) => {
  const { t } = useTranslation();
  const { user, currentProfile } = useUserContext();
  const { isRestricted } = useProfileRestriction();
  const navigate = useNavigate();
  const { updateSetting } = useUserSettings({ onError });
  const { progress, handleScroll } = useScrollFadeProgress();

  const { premiumSubscription, userSettings } = user ?? {};
  const isSubscribed = premiumSubscription?.isActive ?? false;
  const isAdvertisingEnabled = userSettings?.isAdvertisingEnabled ?? false;
  const isCommercialNewsSubscriber =
    userSettings?.isCommercialNewsSubscriber ?? false;
  const isParent = !!user?.personType && PARENT_ROLES.has(user.personType);
  const childName = getFullName(currentProfile?.user);
  const bodyName = isParent
    ? t('profileMenu.parentChild', { name: childName })
    : childName;
  const description = joinNonEmpty([
    currentProfile?.user.className,
    currentProfile?.user.schoolName,
  ]);

  const navigateToSubscription = () => {
    navigate(ROUTE_SUBSCRIPTION);
    onClose();
  };

  return (
    <>
      <div className="relative flex shrink-0 items-center bg-background px-4 pt-[calc(16px+env(safe-area-inset-top))] pb-3 md:justify-between md:p-5">
        <button
          type="button"
          onClick={onClose}
          aria-label={t('global.back')}
          className="flex size-6 shrink-0 cursor-pointer items-center justify-center text-foreground md:hidden"
        >
          <ArrowLeft />
        </button>
        <h2
          style={{ opacity: progress }}
          className="absolute left-1/2 -translate-x-1/2 text-callout-16 text-foreground md:hidden"
        >
          {t('profileMenu.title')}
        </h2>
        <h2 className="hidden text-headline-20 text-foreground md:block">
          {t('profileMenu.title')}
        </h2>
        <button
          type="button"
          onClick={onClose}
          aria-label={t('global.close')}
          className="hidden size-6 shrink-0 cursor-pointer items-center justify-center text-foreground md:flex"
        >
          <Close />
        </button>
      </div>

      <div
        onScroll={handleScroll}
        className="flex min-h-0 flex-1 flex-col gap-2 overflow-y-auto bg-background pl-4 max-md:px-4 md:pt-2 md:pr-2"
      >
        <h2
          style={{ opacity: 1 - progress }}
          className="pt-1 pb-2 text-title-36 text-foreground transition-opacity duration-[50ms] md:hidden"
        >
          {t('profileMenu.title')}
        </h2>
        {user && (
          <div className="flex flex-col gap-3 rounded-xl bg-card p-4 shadow-card">
            <p className="text-callout-16 text-foreground capitalize">
              {getRoleLabel(user.personType)}
            </p>
            <div className="flex flex-col gap-1">
              <p className="text-body-14 text-foreground">{bodyName}</p>
              {description && (
                <p className="text-body-14 text-subtle-foreground">
                  {description}
                </p>
              )}
            </div>
          </div>
        )}

        <div className="flex flex-col rounded-xl bg-card shadow-card">
          <Cell
            icon={IconType.Person}
            title={t('profileMenu.username')}
            subtitle={user?.username}
            onClick={() => onOpenView(ProfileMenuView.Username)}
          />
          <div className="mx-3 h-px bg-divider" />
          <Cell
            icon={IconType.Phone}
            title={t('profileMenu.phone')}
            subtitle={user?.phoneNumber}
            onClick={() => onOpenView(ProfileMenuView.Phone)}
          />
          <div className="mx-3 h-px bg-divider" />
          <Cell
            icon={IconType.Mail}
            title={t('profileMenu.mail')}
            subtitle={user?.emailAddress}
            onClick={() => onOpenView(ProfileMenuView.Email)}
          />
          <div className="mx-3 h-px bg-divider" />
          <Cell
            icon={IconType.Lock}
            title={t('profileMenu.password')}
            onClick={() => onOpenView(ProfileMenuView.Password)}
          />
          <div className="mx-3 h-px bg-divider" />
          <Cell
            icon={IconType.Group}
            title={t('profileMenu.familySettings')}
            onClick={() => onOpenView(ProfileMenuView.Family)}
          />
          <div className="mx-3 h-px bg-divider" />
          <Cell
            icon={IconType.Bell}
            title={t('profileMenu.notifications')}
            onClick={() => onOpenView(ProfileMenuView.Notifications)}
          />
          {!isRestricted && (
            <>
              <div className="mx-3 h-px bg-divider" />
              <Cell
                icon={IconType.Subscription}
                title={t('profileMenu.subscription')}
                onClick={navigateToSubscription}
              />
              <div className="mx-3 h-px bg-divider" />
              <Cell
                icon={IconType.Advertisement}
                title={t('profileMenu.ads')}
                subtitle={t('profileMenu.adsDescription')}
                trailing={
                  <Toggle
                    checked={isAdvertisingEnabled}
                    disabled={!isSubscribed}
                    label={t('profileMenu.ads')}
                    onCheckedChange={(value) =>
                      updateSetting({ setting: UserSetting.Advertising, value })
                    }
                  />
                }
              />
              <div className="mx-3 h-px bg-divider" />
              <Cell
                icon={IconType.Advertisement}
                title={t('profileMenu.commercialNews')}
                subtitle={t('profileMenu.commercialNewsDescription')}
                trailing={
                  <Toggle
                    checked={isCommercialNewsSubscriber}
                    disabled={!isSubscribed}
                    label={t('profileMenu.commercialNews')}
                    onCheckedChange={(value) =>
                      updateSetting({
                        setting: UserSetting.CommercialNews,
                        value,
                      })
                    }
                  />
                }
              />
            </>
          )}
          <div className="mx-3 h-px bg-divider" />
          <Cell
            icon={IconType.Logout}
            title={t('global.logout')}
            onClick={onLogout}
          />
        </div>
        <div className="h-4 shrink-0" />
      </div>
    </>
  );
};
